import { Logo } from '@common/components/brand/Logo';
import type { Role } from '@features/auth';
import { useAuth } from '@features/auth';
import { useCampanhaAtiva } from '@features/formulario';
import { Sidebar, useSidebar } from '@ntconsult/aluma-react';
import { CalendarRange, ClipboardList, Home, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useMatch } from 'react-router-dom';

type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  allowedRoles?: Role[];
  hidden?: boolean;
};

function NavItemLink({ item }: { item: NavItem }) {
  // `end` no início: senão "Início" (`/`) ficaria ativo em todas as rotas.
  const match = useMatch({ path: item.to, end: item.to === '/' });
  return (
    <Sidebar.Item asChild icon={item.icon} active={!!match}>
      <NavLink to={item.to}>{item.label}</NavLink>
    </Sidebar.Item>
  );
}

function SidebarBrand() {
  const { t } = useTranslation('common');
  const { open, collapsible } = useSidebar();
  const collapsed = collapsible !== 'none' && !open;
  return (
    <Sidebar.Header className={collapsed ? 'justify-center' : undefined}>
      <Logo iconOnly size="md" />
      {!collapsed && <span className="text-base font-semibold">{t('appName')}</span>}
    </Sidebar.Header>
  );
}

/** Versão do app no rodapé da sidebar — sutil; some quando colapsada. */
function SidebarVersion() {
  const { open, collapsible } = useSidebar();
  const collapsed = collapsible !== 'none' && !open;
  if (collapsed) return null;
  return (
    <Sidebar.Footer>
      <span className="text-xs text-foreground-subtle">v{__APP_VERSION__}</span>
    </Sidebar.Footer>
  );
}

export function AppSidebar() {
  const { t } = useTranslation('common');
  const { open, isMobile } = useSidebar();
  const { user } = useAuth();

  // Quem não é administrador vai de `/` direto ao formulário enquanto há campanha ativa
  // (spec 05 R27); "Início" não abriria nada, então some do menu. Enquanto a campanha ainda
  // está sendo consultada também fica oculto, para o item não aparecer e sumir.
  const ehAdmin = !!user?.roles.includes('administrador');
  const ativa = useCampanhaAtiva(!!user && !ehAdmin);
  const inicioOculto = !ehAdmin && (ativa.isLoading || !!ativa.data);

  const items: NavItem[] = [
    { to: '/', label: t('nav.inicio'), icon: <Home />, hidden: inicioOculto },
    { to: '/responder', label: t('nav.responder'), icon: <ClipboardList /> },
    {
      to: '/campanhas',
      label: t('nav.campanhas'),
      icon: <CalendarRange />,
      allowedRoles: ['administrador'],
    },
    {
      to: '/usuarios',
      label: t('nav.usuarios'),
      icon: <Users />,
      allowedRoles: ['administrador'],
    },
  ];

  const visible = items.filter(
    (i) =>
      !i.hidden &&
      (!i.allowedRoles ||
        i.allowedRoles.length === 0 ||
        user?.roles.some((r) => i.allowedRoles?.includes(r))),
  );

  return (
    // Fechada em mobile o painel só sai da tela (translate): `invisible` tira os links do Tab.
    <Sidebar ariaLabel={t('nav.sidebar')} className={isMobile && !open ? 'invisible' : undefined}>
      <SidebarBrand />
      <Sidebar.Nav>
        {visible.map((item) => (
          <NavItemLink key={item.to} item={item} />
        ))}
      </Sidebar.Nav>
      <SidebarVersion />
    </Sidebar>
  );
}
